// Cross-channel switch. Swap the desktop app between the Stable and
// Release Candidates servers without forcing the user to re-enter a
// magic code on the other side.
//
// Flow:
//   1. Ask the CURRENT server to mint a short-lived HMAC-signed token
//      bound to the player's email and the target channel.
//   2. Flip the saved channel pref so subsequent requests resolve the
//      new server URL.
//   3. Redeem the token on the NEW server. It verifies the signature,
//      finds or creates the player by email, and returns a fresh
//      session token for itself.
//   4. Store the new token so the next renderer load auto-authenticates.
//   5. Reload the renderer. The WebSocket is dropped, every cached
//      profile/leaderboard/etc. state resets, and the app boots cleanly
//      against the new server with the new token.
//
// The reload is intentional. Server-specific in-memory state
// (collection, decks, match history) is wiped from the renderer rather
// than trying to live-swap dozens of subscribers to the new server.

import { CHANNEL_URLS, setUpdateChannel, getUpdateChannel, getServerUrl } from './updateChannel';
import { getStoredToken, setStoredToken } from './authApi';

export class CrossChannelSwitchError extends Error {
  constructor(message, { stage, status, cause } = {}) {
    super(message);
    this.name = 'CrossChannelSwitchError';
    this.stage = stage || 'unknown';
    // HTTP status of the failing call when known. 404 typically means
    // the server hasn't been deployed with the bridge yet; 403 means
    // the server explicitly rejected (e.g. not on the RC allowlist).
    this.status = typeof status === 'number' ? status : null;
    if (cause) this.cause = cause;
  }
}

// Mint a cross-channel token from the current server, switch the
// channel pref, redeem the token on the new server, save the new
// session token, and reload. Throws CrossChannelSwitchError on any
// failure step; the caller is expected to surface the message and
// leave the user on the current channel (no rollback needed because
// the only side-effect prior to the channel flip is a single
// authenticated POST to /auth/cross-channel/issue).
export async function switchChannel(targetChannel, { reload = true } = {}) {
  if (targetChannel !== 'stable' && targetChannel !== 'beta' && targetChannel !== 'local') {
    throw new CrossChannelSwitchError('Invalid target channel', { stage: 'validate' });
  }
  const current = getUpdateChannel();
  if (current === targetChannel) return { switched: false };

  // The 'local' channel can't use the cross-channel bridge because
  // local dev doesn't share the HMAC secret with prod and probably
  // doesn't have the user's account. Fall through to the caller's
  // logout-and-relog flow for that case.
  if (targetChannel === 'local' || current === 'local') {
    throw new CrossChannelSwitchError(
      'Local dev cannot use the cross-channel bridge. Sign out manually',
      { stage: 'validate' }
    );
  }

  // 1) Mint token on the current server. Use a direct fetch so we
  //    can inspect the HTTP status. `api.post` flattens everything
  //    into a generic Error and we need to tell 404 (old server)
  //    apart from 403 (allowlist rejection).
  const sessionToken = await getStoredToken();
  let issueResult;
  try {
    const res = await fetch(`${getServerUrl()}/auth/cross-channel/issue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
      },
      body: JSON.stringify({ target: targetChannel }),
    });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { const data = await res.json(); if (data?.error) msg = data.error; } catch {}
      throw new CrossChannelSwitchError(msg, { stage: 'issue', status: res.status });
    }
    issueResult = await res.json();
  } catch (err) {
    if (err instanceof CrossChannelSwitchError) throw err;
    throw new CrossChannelSwitchError(
      err?.message || 'Failed to issue cross-channel token',
      { stage: 'issue', cause: err }
    );
  }
  const token = issueResult?.token;
  if (!token) {
    throw new CrossChannelSwitchError('Cross-channel token missing in response', { stage: 'issue' });
  }

  // 2) Flip the channel pref. After this point serverClient.api would
  //    target the new server too, but we use a direct fetch below
  //    because the /verify call is unauthenticated and we want to
  //    isolate it from any cached auth header.
  setUpdateChannel(targetChannel);

  // 3) Redeem on the new server via direct fetch so a stale stored
  //    token can't leak into the Authorization header.
  const newBase = CHANNEL_URLS[targetChannel].http;
  let verifyData;
  let verifyStatus = null;
  try {
    const res = await fetch(`${newBase}/auth/cross-channel/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    verifyStatus = res.status;
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { const data = await res.json(); if (data?.error) msg = data.error; } catch {}
      throw new Error(msg);
    }
    verifyData = await res.json();
  } catch (err) {
    // Roll the channel pref back so the user isn't stranded on the
    // new server with no usable token.
    setUpdateChannel(current);
    throw new CrossChannelSwitchError(
      err?.message || 'Failed to verify cross-channel token on new server',
      { stage: 'verify', status: verifyStatus, cause: err }
    );
  }

  if (!verifyData?.token) {
    setUpdateChannel(current);
    throw new CrossChannelSwitchError(
      'Cross-channel verify returned no session token',
      { stage: 'verify' }
    );
  }

  // 4) Save the new session token for the next renderer load.
  try {
    await setStoredToken(verifyData.token);
  } catch (err) {
    throw new CrossChannelSwitchError(
      'Failed to persist new session token',
      { stage: 'store-token', cause: err }
    );
  }

  // 5) Reload. Electron / browser both reload the same way. Skipping
  //    the reload (reload: false) is useful for unit tests.
  if (reload && typeof window !== 'undefined' && window.location?.reload) {
    window.location.reload();
  }

  return { switched: true, profile: verifyData.profile };
}
